import { Theme } from 'common/graphqlTypes';
import { AvailableTheme } from 'common/types';
import css from 'styles/_export.module.scss';

const color = css as Record<string, string>;

/**
 * The purpose of this file is to consolidate JS-based styling in the scalyr web application.
 * Here are the following ideas the structure below bases on:
 * - Every component has its style set.
 *   This assumption is the reason for splitting themes by components
 *   and using the `THEMED_COMPONENT` enum to list them all.
 * - We want our components' styles to be as simple as possible. To achieve simplicity, we use maps.
 * - In every theme, we need a complete set of styling properties.
 *   The main scalyr theme is supposed to have all of them, and other themes should correspond to it.
 *   The `Theme` type ensures each style's presence in each component's style set
 *   and each component's style set appearance in each theme.
 *
 * TypeScript settings in this file will help you with the following potential mistakes:
 * - New component added to one of the themes and is absent in other;
 * - New style added to one of the themes and missing in other.
 */

export type ThemeInfo = {
  label:       string;
  switchLabel: string;
  pairedWith:  AvailableTheme;
  isDark:      boolean;
};

// Theme info is needed for the switch in the user menu
export const THEME_INFO: Record<AvailableTheme, ThemeInfo> = {
  [AvailableTheme.SENTINEL_ONE]: {
    label:       'Light',
    switchLabel: '',
    pairedWith:  AvailableTheme.SENTINEL_ONE_DARK,
    isDark:      false,
  },
  [AvailableTheme.SENTINEL_ONE_DARK]: {
    label:       'Dark',
    switchLabel: 'Dark mode',
    pairedWith:  AvailableTheme.SENTINEL_ONE,
    isDark:      true,
  },
  [AvailableTheme.SINGULARITY]: {
    label:       'Light',
    switchLabel: '',
    pairedWith:  AvailableTheme.SINGULARITY_DARK,
    isDark:      false,
  },
  [AvailableTheme.SINGULARITY_DARK]: {
    label:       'Dark',
    switchLabel: 'Dark mode',
    pairedWith:  AvailableTheme.SINGULARITY,
    isDark:      true,
  },
};

/**
 * Converts between SentinelOne and Singularity themes and ensures that the theme is one we know about.
 *
 * @param theme          the theme to adjust
 *
 * @returns possibly adjusted theme
 */
export const adjustTheme = (theme: Theme, isSingularityThemeEnabled: boolean): AvailableTheme => {
  let adjustedTheme = theme as unknown as AvailableTheme;
  let themeInfo = THEME_INFO[adjustedTheme];
  if (!themeInfo) {
    // not a known theme, switch to one of the two default themes
    adjustedTheme = theme.endsWith('dark') ? AvailableTheme.SENTINEL_ONE_DARK : AvailableTheme.SENTINEL_ONE;
    themeInfo = THEME_INFO[adjustedTheme];
  }
  if (isSingularityThemeEnabled) {
    adjustedTheme = themeInfo.isDark ? AvailableTheme.SINGULARITY_DARK : AvailableTheme.SINGULARITY;
  }

  return adjustedTheme;
};

export enum THEMED_COMPONENT {
  ANNOTATIONS         = 'annotations',
  CODE_MIRROR         = 'codeMirror',
  DISTRIBUTION_GRAPH  = 'distributionGraph',
  HIGHCHARTS          = 'highcharts',
  ICON                = 'icon',
  LINE_CHART_SKELETON = 'lineChartSkeleton',
  PLOTS               = 'plots',
  SPARKLINE_GRAPH     = 'sparklineGraph',
  USAGE_GRAPH         = 'usageGraph',
}

export enum ICON_COLORS {
  NONE                            = 'none',
  ICOMOON                         = 'icomoon',
  INFO                            = 'info',
  WARNING                         = 'warning',
  ERROR                           = 'error',
  ENDPOINT                        = 'endpoint',
  INFECTED_ENDPOINT               = 'infectedEndpoint',
  INACTIVE_ENDPOINT               = 'inactiveEndpoint',
  INACTIVE_THREAT_ACTIVE_ENDPOINT = 'inactiveThreatActiveEndpoint',
  LINK                            = 'link',
}

const SCALYR_DEFAULT_THEME = {

  [THEMED_COMPONENT.ANNOTATIONS]: {
    0: {
      borderColor:     color.black,
      backgroundColor: 'rgba(0,0,0,0.2)',
    },
    1: {
      borderColor:     '#777777',
      backgroundColor: 'rgba(119,119,119,0.2)',
    },
    2: {
      borderColor:     '#228b22',
      backgroundColor: 'rgba(34,139,34,0.2)',
    },
    3: {
      borderColor:     '#32cd32',
      backgroundColor: 'rgba(50,205,50,0.2)',
    },
    4: {
      borderColor:     '#36a39e',
      backgroundColor: 'rgba(54,163,158,0.2)',
    },
    5: {
      borderColor:     '#06A8F6',
      backgroundColor: 'rgba(6,168,246,0.2)',
    },
    6: {
      borderColor:     '#4169e1',
      backgroundColor: 'rgba(65,105,225,0.2)',
    },
    7: {
      borderColor:     '#000080',
      backgroundColor: 'rgba(0,0,128,0.2)',
    },
    8: {
      borderColor:     '#8a2be2',
      backgroundColor: 'rgba(138,43,226,0.2)',
    },
    9: {
      borderColor:     '#C320D0',
      backgroundColor: 'rgba(195,32,208,0.2)',
    },
    10: {
      borderColor:     '#ff1493',
      backgroundColor: 'rgba(255,20,147,0.2)',
    },
    11: {
      borderColor:     '#ff0000',
      backgroundColor: 'rgba(255,0,0,0.2)',
    },
    12: {
      borderColor:     '#ff7f50',
      backgroundColor: 'rgba(255,127,80,0.2)',
    },
    13: {
      borderColor:     '#ffa500',
      backgroundColor: 'rgba(255,165,0,0.2)',
    },
  },

  [THEMED_COMPONENT.CODE_MIRROR]: {
    theme: 'neo',
  },

  [THEMED_COMPONENT.DISTRIBUTION_GRAPH]: {
    distributionSeriesColorStop0:  '#00BDD6',
    distributionSeriesColorStop1:  '#10B1C6',
  },

  [THEMED_COMPONENT.HIGHCHARTS]: {
    chartBackground:     color.white,
    title:               color.gray900,
    legendItem:          '#666666',
    xAxisGridLine:       '#e6e6e6',
    xAxisLabel:          '#666666',
    xAxisLine:           '#ccd6eb',
    xAxisMinorGridLine:  '#f2f2f2',
    xAxisTick:           '#ccd6eb',
    yAxisGridLine:       '#e6e6e6',
    yAxisLabel:          '#666666',
    yAxisLine:           '#ccd6eb',
    yAxisMinorGridLine:  '#f2f2f2',
    yAxisTick:           '#ccd6eb',
    tooltip:             '#333333',
    tooltipBackground:   'rgba(255, 255, 255, 0.75)',
    // The plotSeriesDataLabel could be either a color written in string or undefined
    // when we want to bring it to the default state.
    //  - Using '' breaks during the runtime for pie charts.
    //  - When trying to use a plotSeriesDataLabel?:, you encounter a TypeScript error.
    // The described problems led to this unusual TypeScript approach.
    plotSeriesDataLabel: undefined as string | undefined,
    plotSeriesMarker:    color.white,
    plotPieBorder:       color.white,
    columnFillColor:     color.scalyrData1,
    columnHoverColor:    color.scalyrData2,
    plotSeriesLineColor: color.black025,
    xAxisCrossHairLine:  color.black040,
    plotLineMarkerColor: color.black050,
  },
  // TODO: Replace all color to S1 Console’s color palette const.
  [THEMED_COMPONENT.ICON]: {
    [ICON_COLORS.ICOMOON]:                          '',
    [ICON_COLORS.INFO]:                             '#44BCA4',
    [ICON_COLORS.WARNING]:                          '#FFBB34',
    [ICON_COLORS.ERROR]:                            '#FF5B00',
    [ICON_COLORS.ENDPOINT]:                         '#757575',
    [ICON_COLORS.INFECTED_ENDPOINT]:                '#ED001B',
    [ICON_COLORS.INACTIVE_ENDPOINT]:                '#C4C4C4',
    [ICON_COLORS.INACTIVE_THREAT_ACTIVE_ENDPOINT]:  '#FAB9C9',
    [ICON_COLORS.LINK]:                             '#7B3FF2',
  },

  [THEMED_COMPONENT.LINE_CHART_SKELETON]: {
    gridColor: color.gray400,
    plotColor: color.gray500,
  },

  [THEMED_COMPONENT.PLOTS]: {
    0:  '#00BDD6',
    1:  '#FD5821',
    2:  '#3E51B5',
    3:  '#D33682',
    4:  '#89C34A',
    5:  '#653AB8',
    6:  '#06A8F6',
    7:  '#FD9802',
    8:  '#029788',
    9:  '#E10009',
    10: '#C3CE08',
    11: '#5E7E8C',
    12: '#9B28B1',
    13: '#775547',
    14: '#859900',
    15: '#073642',
    16: '#268BD2',
    17: '#657B83',
    18: '#B58900',
    19: '#93A1A1',
  },

  [THEMED_COMPONENT.SPARKLINE_GRAPH]: {
    chartBackgroundColor:        '',
    tooltipBackgroundColor:      'rgba(255, 255, 255, 0.65)',
    tooltipTextColor:            'rgba(0, 0, 0, 0.8)',
    columnNegativeColor:         '#910000',
    columnBorderColor:           'silver',
    seriesFillOpacity:           0.25,
    seriesPreviousDataFillColor: '#cccccc',
    defaultPlotColor:            '#BBBBBB',
    desiredTrend:                '#444444',
  },

  [THEMED_COMPONENT.USAGE_GRAPH]: {
    primaryColor:    color.blue500,
    thresholdColor:  color.red500,
    backgroundColor: '#EFEFEF',
  },
};

export type ComponentsTheme = {[Component in THEMED_COMPONENT]: typeof SCALYR_DEFAULT_THEME[Component]};
export type ComponentTheme<Component extends THEMED_COMPONENT> = ComponentsTheme[Component];
export type Themes = Record<AvailableTheme, ComponentsTheme>;

export const themes: Themes = {

  /**
   * This mapping is required to utilize TypeScript's warnings about the set of supported components.
   * It protects from accidentally adding a new component to the default theme
   * and forgetting to add it to other themes.
   * Simple correspondence `scalyr: SCALYR_DEFAULT_THEME` or `scalyr: { ...SCALYR_DEFAULT_THEME }` do not work this way.
   */
  [AvailableTheme.SENTINEL_ONE]: {

    [THEMED_COMPONENT.ANNOTATIONS]: {
      ...SCALYR_DEFAULT_THEME.annotations,
    },

    [THEMED_COMPONENT.CODE_MIRROR]: {
      ...SCALYR_DEFAULT_THEME.codeMirror,
    },

    [THEMED_COMPONENT.DISTRIBUTION_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.distributionGraph,
    },

    [THEMED_COMPONENT.HIGHCHARTS]: {
      ...SCALYR_DEFAULT_THEME.highcharts,
      title:               color.dsGray900, // Chart title in PDF export
      legendItem:          color.dsGray900, // Used in PDF export?
      xAxisLabel:          color.dsGray600, // Text color along x-axis
      xAxisLine:           color.dsGray300, // X-axis baseline
      xAxisTick:           color.dsGray300, // Tick marks along x-axis
      xAxisCrossHairLine:  color.black040,
      yAxisGridLine:       color.dsGray200, // Horizontal lines in grid?
      yAxisLabel:          color.dsGray600, // Text color along y-axis
      tooltip:             color.dsGray900, // Text on hover panel
      tooltipBackground:   'rgba(255, 255, 255, 0.8)',
      columnFillColor:     color.dsData300,
      columnHoverColor:    color.dsData400,
      plotSeriesLineColor: color.black025,
      plotLineMarkerColor: color.black050,
    },

    [THEMED_COMPONENT.ICON]: {
      ...SCALYR_DEFAULT_THEME.icon,
      [ICON_COLORS.ICOMOON]: color.dsGray900, // same as 'text'
    },

    [THEMED_COMPONENT.LINE_CHART_SKELETON]: {
      ...SCALYR_DEFAULT_THEME.lineChartSkeleton,
    },

    [THEMED_COMPONENT.PLOTS]: {
      ...SCALYR_DEFAULT_THEME.plots,
    },

    [THEMED_COMPONENT.SPARKLINE_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.sparklineGraph,
    },

    [THEMED_COMPONENT.USAGE_GRAPH]: {
      primaryColor:    color.dsGray300,
      thresholdColor:  color.dsRed500,
      backgroundColor: 'transparent',
    },
  },

  [AvailableTheme.SENTINEL_ONE_DARK]: {

    [THEMED_COMPONENT.ANNOTATIONS]: {
      ...SCALYR_DEFAULT_THEME.annotations,
    },

    [THEMED_COMPONENT.CODE_MIRROR]: {
      theme: 'material',
    },

    [THEMED_COMPONENT.DISTRIBUTION_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.distributionGraph,
    },

    [THEMED_COMPONENT.HIGHCHARTS]: {
      chartBackground:     color.darkGray800,
      title:               color.whiteO85, // Chart title in PDF export
      legendItem:          color.whiteO70,
      xAxisGridLine:       color.whiteO30,
      xAxisLabel:          color.whiteO70, // Text color along x-axis
      xAxisLine:           color.whiteO30, // X axis
      xAxisMinorGridLine:  color.whiteO30,
      xAxisTick:           color.whiteO30, // Tick marks along x-axis
      yAxisGridLine:       color.whiteO20, // Grid lines graph field
      yAxisLabel:          color.whiteO70, // Text color along y-axis
      yAxisLine:           color.whiteO30,
      yAxisMinorGridLine:  color.whiteO30,
      yAxisTick:           color.whiteO30,
      tooltip:             color.white, // Text on hover panel
      tooltipBackground:   color.black,
      plotSeriesDataLabel: color.whiteO70,
      plotSeriesMarker:    color.white,
      plotPieBorder:       color.white,
      columnFillColor:     color.dsdData300,
      columnHoverColor:    color.dsdData400,
      plotSeriesLineColor: color.white025,
      xAxisCrossHairLine:  color.white030,
      plotLineMarkerColor: color.white025,
    },

    [THEMED_COMPONENT.ICON]: {
      ...SCALYR_DEFAULT_THEME.icon,
      [ICON_COLORS.ICOMOON]: color.white085, // same as 'text'
    },

    [THEMED_COMPONENT.LINE_CHART_SKELETON]: {
      ...SCALYR_DEFAULT_THEME.lineChartSkeleton,
    },

    [THEMED_COMPONENT.PLOTS]: {
      // These are the colors from the 'scalyr' theme lightened by 5 and saturated by 5.
      0:  '#00d4f0',
      1:  '#ff6a39',
      2:  '#455ac7',
      3:  '#dd458f',
      4:  '#96cf58',
      5:  '#7041cb',
      6:  '#17b3ff',
      7:  '#ffa31a',
      8:  '#00b3a1',
      9:  '#fb000a',
      10: '#e0ed03',
      11: '#638ea1',
      12: '#b127cc',
      13: '#8c5e4b',
      14: '#9bb300',
      15: '#074a5c',
      16: '#3198e1',
      17: '#6a8b97',
      18: '#cf9c00',
      19: '#9cb1b1',
    },

    [THEMED_COMPONENT.SPARKLINE_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.sparklineGraph,
    },

    [THEMED_COMPONENT.USAGE_GRAPH]: {
      primaryColor:    color.dsdBlue500,
      thresholdColor:  color.dsdRed500,
      backgroundColor: color.whiteO20,
    },
  },

  [AvailableTheme.SINGULARITY]: {

    [THEMED_COMPONENT.ANNOTATIONS]: {
      ...SCALYR_DEFAULT_THEME.annotations,
    },

    [THEMED_COMPONENT.CODE_MIRROR]: {
      ...SCALYR_DEFAULT_THEME.codeMirror,
    },

    [THEMED_COMPONENT.DISTRIBUTION_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.distributionGraph,
    },

    [THEMED_COMPONENT.HIGHCHARTS]: {
      ...SCALYR_DEFAULT_THEME.highcharts,
      title:               color.s1Gray900, // Chart title in PDF export s1-gray-900
      legendItem:          color.s1Gray900, // Used in PDF export?
      xAxisLabel:          color.s1Gray600, // Text color along x-axis
      xAxisLine:           color.s1Gray300, // X-axis baseline
      xAxisTick:           color.s1Gray300, // Tick marks along x-axis
      xAxisCrossHairLine:  color.black040,
      yAxisGridLine:       color.s1Gray200, // Horizontal lines in grid?
      yAxisLabel:          color.s1Gray600, // Text color along y-axis
      tooltip:             color.s1Gray900, // Text on hover panel
      tooltipBackground:   'rgba(255, 255, 255, 0.8)',
      columnFillColor:     color.s1Data700,
      columnHoverColor:    color.s1Data700,
      plotSeriesLineColor: color.black025,
      plotLineMarkerColor: color.black050,
    },

    [THEMED_COMPONENT.ICON]: {
      ...SCALYR_DEFAULT_THEME.icon,
      [ICON_COLORS.ICOMOON]: color.s1Gray900, // same as 'text'
      [ICON_COLORS.LINK]: color.s1Purple400,
    },

    [THEMED_COMPONENT.LINE_CHART_SKELETON]: {
      ...SCALYR_DEFAULT_THEME.lineChartSkeleton,
    },

    [THEMED_COMPONENT.PLOTS]: {
      ...SCALYR_DEFAULT_THEME.plots,
    },

    [THEMED_COMPONENT.SPARKLINE_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.sparklineGraph,
    },

    [THEMED_COMPONENT.USAGE_GRAPH]: {
      primaryColor:    color.s1Gray300,
      thresholdColor:  color.s1Red500,
      backgroundColor: 'transparent',
    },
  },

  [AvailableTheme.SINGULARITY_DARK]: {

    [THEMED_COMPONENT.ANNOTATIONS]: {
      ...SCALYR_DEFAULT_THEME.annotations,
    },

    [THEMED_COMPONENT.CODE_MIRROR]: {
      theme: 'material',
    },

    [THEMED_COMPONENT.DISTRIBUTION_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.distributionGraph,
    },

    [THEMED_COMPONENT.HIGHCHARTS]: {
      chartBackground:     color.slate900, // Background, applied for printed charts only
      title:               color.whiteO85, // Chart title in PDF export
      legendItem:          color.whiteO70,
      xAxisGridLine:       color.whiteO30,
      xAxisLabel:          color.whiteO70, // Text color along x-axis
      xAxisLine:           color.whiteO30, // X axis
      xAxisMinorGridLine:  color.whiteO30,
      xAxisTick:           color.whiteO30, // Tick marks along x-axis
      yAxisGridLine:       color.whiteO20, // Grid lines graph field
      yAxisLabel:          color.whiteO70, // Text color along y-axis
      yAxisLine:           color.whiteO30,
      yAxisMinorGridLine:  color.whiteO30,
      yAxisTick:           color.whiteO30,
      tooltip:             color.white, // Text on hover panel
      tooltipBackground:   color.black,
      plotSeriesDataLabel: color.whiteO70,
      plotSeriesMarker:    color.white,
      plotPieBorder:       color.white,
      columnFillColor:     color.s1dData500,
      columnHoverColor:    color.s1dData500,
      plotSeriesLineColor: color.white025,
      xAxisCrossHairLine:  color.white030,
      plotLineMarkerColor: color.white025,
    },

    [THEMED_COMPONENT.ICON]: {
      ...SCALYR_DEFAULT_THEME.icon,
      [ICON_COLORS.ICOMOON]: color.whiteO85, // same as 'text'
      [ICON_COLORS.LINK]: color.s1dPurple500,
    },

    [THEMED_COMPONENT.LINE_CHART_SKELETON]: {
      ...SCALYR_DEFAULT_THEME.lineChartSkeleton,
    },

    [THEMED_COMPONENT.PLOTS]: {
      // These are the colors from the 'scalyr' theme lightened by 5 and saturated by 5.
      0:  '#00d4f0',
      1:  '#ff6a39',
      2:  '#455ac7',
      3:  '#dd458f',
      4:  '#96cf58',
      5:  '#7041cb',
      6:  '#17b3ff',
      7:  '#ffa31a',
      8:  '#00b3a1',
      9:  '#fb000a',
      10: '#e0ed03',
      11: '#638ea1',
      12: '#b127cc',
      13: '#8c5e4b',
      14: '#9bb300',
      15: '#074a5c',
      16: '#3198e1',
      17: '#6a8b97',
      18: '#cf9c00',
      19: '#9cb1b1',
    },

    [THEMED_COMPONENT.SPARKLINE_GRAPH]: {
      ...SCALYR_DEFAULT_THEME.sparklineGraph,
    },

    [THEMED_COMPONENT.USAGE_GRAPH]: {
      primaryColor:    color.s1dPurple500,
      thresholdColor:  color.s1dRed500,
      backgroundColor: color.whiteO20,
    },
  },
};
